import { DropDown } from '@components/core-ui/form-elements/DropDown';
import Dots from '@components/icons/Dots.svg';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import { getHourPrice } from '@helpers/getHourPrice';
import { isSubscriptionEditable } from '@helpers/isSubscriptionEditable';
import { useAuthContext } from '@providers/AuthProvider';
import { useModalContext } from '@providers/ModalProvider';
import { routes } from '@routes';
import {
  GetAllSubscriptionPlansPlanResponseDto,
  GetBillingAccountResponseDto,
  GetBillingAccountResponseSubscriptionDto,
} from 'app/API';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFreeTrialDaysLeft } from '@pages/User/BillingAccountDetailsPage/helpers/getFreeTrialDaysLeft';
import { DotsButton } from './styles/DotsButton';
import { TextButtonLarge } from '@components/core-ui/form-elements/buttons/Button/variations/TextButtonLarge';
import { Wrapper } from './styles/Wrapper';

type TProps = {
  className?: string;
  reload: () => void;
  plan: GetAllSubscriptionPlansPlanResponseDto;
  subscription: GetBillingAccountResponseSubscriptionDto;
  billingAccount?: GetBillingAccountResponseDto;
};

export const TrialingCard = ({ className = '', plan, subscription, billingAccount, reload }: TProps) => {
  const { openModal, closeModal } = useModalContext();
  const navigate = useNavigate();
  const { organization, updateMe } = useAuthContext();
  const [showDropDown, setShowDropDown] = useState(false);

  const planName = plan?.name.replace('AutoScriptOne ', '');
  const price = getHourPrice(Number(plan?.price), true);
  const daysLeft = getFreeTrialDaysLeft(subscription.freeTrialEndDate, new Date());

  const onEditClick = (e: React.MouseEvent<HTMLButtonElement | HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();
    openModal(ModalNamesEnum.EditASOneSubscriptionModal, {
      subscription,
      onSuccess: () => {
        closeModal();
        if (reload) {
          reload();
        }
      },
    });
  };

  const dropDownOptions: {
    title: string;
    onClick: (e: React.MouseEvent<HTMLDivElement>) => void;
  }[] = [];

  if (organization?.permissions?.viewLicenses) {
    dropDownOptions.push({
      title: 'Details',
      onClick: (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        navigate(routes.autoScriptOneSubscriptionDetails.make(organization?.slug ?? '', subscription.id));
      },
    });
  }

  if (organization?.permissions?.manageLicenses) {
    dropDownOptions.push({
      title: 'Cancel',
      onClick: (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        openModal(ModalNamesEnum.ConfirmSubscriptionCancellation, {
          billingAccountId: billingAccount?.id,
          subscriptionId: subscription.id,
          planName: plan.name,
          onSuccess: () => {
            updateMe();
            reload();
          },
        });
      },
    });
  }

  const renderOptions = () => {
    return (
      <>
        <DotsButton
          onClick={(e) => {
            e.stopPropagation();
            setShowDropDown(true);
          }}
        >
          <Dots />
        </DotsButton>
        {showDropDown ? <DropDown close={() => setShowDropDown(false)} options={dropDownOptions} /> : null}
      </>
    );
  };

  const renderPriceActive = () => {
    const unassignedCount = subscription?.licenses.filter((lic) => lic.assigneeId === null).length;
    const assignedCount = subscription?.licenses.filter((lic) => lic.assigneeId !== null).length;
    return (
      <div className="price-active">
        <div className="left">
          <div>Free trial</div>
          {daysLeft > 1 ? <div>{daysLeft} days</div> : <div>{price}/seat</div>}
        </div>
        <div className="divider"></div>
        <div className="right">
          <div>Active</div>
          <div>
            {assignedCount}/{assignedCount + unassignedCount}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Wrapper className={className}>
      <div className="content">
        <h2 className="as-one">AutoScript</h2>
        <h2 className="plan-name">{planName}</h2>
        {dropDownOptions.length > 0 && renderOptions()}
      </div>

      {renderPriceActive()}

      {organization?.permissions?.manageLicenses && isSubscriptionEditable(subscription.status) ? (
        <div className="buttonContainer">
          <TextButtonLarge onClick={onEditClick}>Edit Subscription</TextButtonLarge>
        </div>
      ) : null}
    </Wrapper>
  );
};
